import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  useNavigate, useLocation, useParams, Navigate,
} from 'react-router';
import CheckAccess from '../../components/check-access/check-access.component';
import StatusBar from '../../components/status-bar/status-bar.component';
import ContentHeader from '../../components/content-header/content-header.component';
import Loader from '../../components/loader/loader.component';
import PageError from '../../components/page-error/page-error.component';
import PageTabs from '../../components/page-tabs/page-tabs.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import LeaveConfirmModal from '../../components/leave-confirm-modal/leave-confirm-modal.component';
import Form from '../../components/form/form.component';
import FormError from '../../components/form-error/form-error.component';
import FormContainer from '../../components/form-container/form-container.component';
import LimitedPageContainer from '../../components/limited-page-block/limited-page-container.component';
import PageContainer from '../../components/page-container/page-container.component';
import PageContainerSide from '../../components/page-container-side/page-container-side.component';
import PageContainerMain from '../../components/page-container-main/page-container-main.component';
import ImageContainer from '../../components/image-container/image-container.component';
import Controls from '../app/controls/controls.component';
import AssetLocationInformation from './asset-location-information/asset-location-information.component';
import AssetPropertyInformation from './asset-property-information/asset-property-information.component';
import AssetPropertyCapitalization from './asset-property-capitalization/asset-property-capitalization.component';
import AssetOwnershipInformation from './asset-ownership-information/asset-ownership-information.component';
import AssetInvestmentInformation from './asset-investment-information/asset-investment-information.component';
import AssetInvestmentPerformance from './asset-investment-performance/asset-investment-performance.component';
import AssetPropertyPerformance from './asset-property-performance/asset-property-performance.component';
import AssetManagementFeeInformation
  from './asset-management-fee-information/asset-management-fee-information.component';
import AssetAllocation from './asset-allocation/asset-allocation.component';
import AssetOther from './asset-other/asset-other.component';
import AssetValidator from './asset.validator';
import FormatService from '../../services/format-service';
import StructureService from '../../services/structure-service';
import FormService from '../../services/form-service';
import DateService from '../../services/date-service';
import GoogleMapService from '../../services/google-map-service';
import useEditModeQuery from '../../hooks/use-edit-mode-query';
import useFormBlock from '../../hooks/use-form-block';
import useFormStructureModified from '../../hooks/use-form-structure-modified';
import useServerFormErrors from '../../hooks/use-server-form-errors';
import { toggleEditMode } from '../app/controls/controls.action';
import { getAssetData, saveAssetData, resetAssetData } from './asset.action';
import { deleteAssetList } from '../asset-list/asset-list.action';
import { getListContactCompanyData, getListTagData } from '../app/search-list/search-list.action';
import { resetFormErrors } from '../app/app.action';
import { FORM_ID_ASSET } from '../../constants/system/form-ids';
import { ASSET_EDIT, ASSET_VIEW, ASSET_DELETE } from '../../constants/entity/permissions';
import {
  ROUTE_ASSET, ROUTE_ASSET_LIST, ROUTE_CONTACT, ROUTE_SEARCH, ROUTE_PARAM_ID, ROUTE_NOT_FOUND, ROUTE_DEAL,
} from '../../constants/system/routes';
import { QUERY_PARAM_TAB, QUERY_PARAM_TAG_ID } from '../../constants/system/query-params';
import {
  TAB_NAME_LOCATION_INFORMATION, TAB_NAME_PROPERTY_INFORMATION, TAB_NAME_PROPERTY_CAPITALIZATION,
  TAB_NAME_OWNERSHIP_INFORMATION, TAB_NAME_INVESTMENT_INFORMATION, TAB_NAME_INVESTMENT_PERFORMANCE,
  TAB_NAME_PROPERTY_PERFORMANCE, TAB_NAME_MANAGEMENT_FEE_INFORMATION, TAB_NAME_OTHER, TAB_NAME_ALLOCATION,
} from '../../constants/system/tab-names';
import { TAB_INDEX_DEFAULT } from '../../constants/system/tab-indexes';
import { TIMEOUT_SHORT } from '../../constants/system/timeouts';
import AssetDefaultImage from '../../static/images/asset-default-image.png';

const Asset: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isDataLoaded,
    isRightDrawerOpen,
    assetData,
    portfolioList,
    hiddenPortfolioList,
    assetStatusList,
    assetOwnershipStructureTypeList,
    contactCompanyList,
    measureUnitList,
    pricingTypeList,
    assetTypeList,
    tagList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const routeParams: ReactRouterParams = useParams();
  const location: ReactRouterLocation = useLocation();
  const navigate: ReactRouterNavigate = useNavigate();
  const dispatch: ReduxDispatch = useDispatch();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const assetId: number = FormatService.parseInt(routeParams[ROUTE_PARAM_ID]);
  const pageTitle: string = `Asset${assetData ? `: ${assetData.name}` : ''}`;
  const formPrefix: string = 'assetData';
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;

  const isMultiplePortfolioGroupView: boolean = useMemo((): boolean => (
    portfolioList && portfolioList.length ? portfolioList
      .map((portfolioItem: Portfolio): number => portfolioItem.group && portfolioItem.group.id)
      .filter((portfolioGroupId: number, portfolioGroupIndex: number, portfolioGroupIdList: number[]): boolean => (
        portfolioGroupIdList.indexOf(portfolioGroupId) === portfolioGroupIndex
      )).length > 1 : false
  ), [portfolioList]);
  const assetViewPermissions: string[] = useMemo((): string[] => [ASSET_VIEW], []);
  const assetEditPermissions: string[] = useMemo((): string[] => [ASSET_EDIT], []);

  const [selectedAssetTypes, setSelectedAssetTypes]: [AssetType[] | null, Function] = useState(null);
  const [selectedTags, setSelectedTags]: [Tag | null, Function] = useState(null);
  const [selectedSellers, setSelectedSellers]: [Contact[] | null, Function] = useState(null);
  const [isNewSellerVisible, setIsNewSellerVisible]: [boolean, Function] = useState(false);
  const [assetTags, setAssetTags]: [Tag[], Function] = useState([]);
  const [assetTypes, setAssetTypes]: [AssetType[], Function] = useState([]);
  const [assetSellers, setAssetSellers]: [Contact[], Function] = useState([]);
  const [assetFieldItems, setAssetFieldItems]: [Record<string, AssetFieldInput> | null, Function]
    = useState<StateValueDefault>(null);
  const [assetFieldNewItemIds, setAssetFieldNewItemIds]: [number[], Function] = useState([]);
  const [assetAllocationItems, setAssetAllocationItems]: [Record<string, AssetAllocationItemInput> | null, Function]
    = useState<StateValueDefault>(null);
  const [assetAllocationNewItemIds, setAssetAllocationNewItemIds]: [number[], Function] = useState([]);
  const [assetPhotoFile, setAssetPhotoFile]: [File | null, Function] = useState(null);
  const [assetAddress, setAssetAddress]: [JsonObject | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [isAssetPhotoDeleted, setIsAssetPhotoDeleted]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastTabIndex, setLastTabIndex]: [number | null, Function] = useState(null);
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);

  const isFormDataPristine: boolean = isFormPristine && !assetPhotoFile && !assetTags.length && !assetTypes.length
    && !assetSellers.length && !isAssetPhotoDeleted;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && (!!lastLocation || (lastTabIndex !== null
    && lastTabIndex >= TAB_INDEX_DEFAULT));
  const deleteConfirmModalTitle: string = 'Delete Asset';
  const deleteConfirmModalText: string = 'Are you sure you want to delete this asset?';

  const onContactCompanySearch: Function = useCallback((query: string): void => {
    dispatch(getListContactCompanyData({
      query,
    }));
  }, []);

  const onContactCompanySearchClose: Function = useCallback((): void => {
    dispatch(getListContactCompanyData({
      query: null,
    }));
  }, []);

  const onAssetPhotoFileChange: Function = useCallback((file: File): void => {
    setAssetPhotoFile(file);
    setIsAssetPhotoDeleted(false);
    changeForm(`${formPrefix}.mainPhoto`, file);
  }, []);

  const onAssetPhotoFileDelete: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setAssetPhotoFile(null);
    setIsAssetPhotoDeleted(true);
    changeForm(`${formPrefix}.mainPhoto`, null);

    event.stopPropagation();
  }, []);

  const onAssetFieldAddNewItem: Function = useCallback((): void => {
    const assetFieldItem: AssetFieldInput = {
      id: StructureService.getNewItemId([], (assetFieldItems && Object.values(assetFieldItems)) || []),
      name: '',
      value: '',
    };

    if (assetFieldItem.id) {
      setAssetFieldItems({
        ...(assetFieldItems || {}),
        [assetFieldItem.id]: assetFieldItem,
      });

      setAssetFieldNewItemIds([
        ...assetFieldNewItemIds,
        assetFieldItem.id,
      ]);
    }
  }, [assetFieldItems, assetFieldNewItemIds]);

  const onAssetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    setAssetTypes(assetTypeValues);
    setSelectedAssetTypes(assetTypeValues);
    changeForm(`${formPrefix}.types`, assetTypeValues);
  }, []);

  const onSellersChange: Function = useCallback((contactValues: Contact): void => {
    setAssetSellers(contactValues);
    setSelectedSellers(contactValues);
    changeForm(`${formPrefix}.sellerIds`, contactValues);
  }, []);

  const onCancelForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);

    setAssetPhotoFile(null);
    setIsAssetPhotoDeleted(false);
    setAssetTypes([]);
    setAssetTags([]);
    setAssetSellers([]);
    setAssetFieldNewItemIds([]);
    setAssetAllocationNewItemIds([]);
    setIsNewSellerVisible(false);
    setErrorMessage(null);
    dispatch(toggleEditMode(false));
  }, []);

  const onDealClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_DEAL}/${dealId}`,
    });

    event.stopPropagation();
  }, []);

  const onContactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  const onTagsChange: Function = useCallback((tagValues: Tag[]): void => {
    setSelectedTags(tagValues);
    setAssetTags(tagValues);
    changeForm(`${formPrefix}.tags`, tagValues);
  }, []);

  const onTagClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tagId: number,
  ): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_TAG_ID]: tagId,
    };

    navigate({
      pathname: ROUTE_SEARCH,
      search: FormatService.formatQueryParams(queryParams),
    });

    event.stopPropagation();
  }, []);

  const onTagSearch: Function = useCallback((query: string): void => {
    dispatch(getListTagData({
      query,
    }));
  }, []);

  const onTagSearchClose: Function = useCallback((): void => {
    dispatch(getListTagData({
      query: null,
    }));
  }, []);

  const onAddressSearchCompleted: Function = useCallback((addressObject: JsonObject): void => {
    setAssetAddress({ ...addressObject });

    changeForm(`${formPrefix}.address`, addressObject);
    changeForm(`${formPrefix}.location`, GoogleMapService.extractLocation(addressObject));
    changeForm(`${formPrefix}.postalCode`, GoogleMapService.extractPostalCode(addressObject));
    changeForm(`${formPrefix}.streetAddress`, GoogleMapService.extractStreetAddress(addressObject));
  }, []);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if (isEditMode) {
      dispatch(toggleEditMode(false));
    }

    return true;
  }, [isFormChanged, isEditMode]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onAddressFieldKeyDown: Function = useCallback((): void => {
    if (assetAddress) {
      changeForm(`${formPrefix}.address`, '');
      changeForm(`${formPrefix}.streetAddress`, '');
      changeForm(`${formPrefix}.location`, '');
      changeForm(`${formPrefix}.postalCode`, '');

      setAssetAddress(null);
    }
  }, [assetAddress]);

  const onAssetFieldDeleteClick: Function = useCallback((assetFieldId: number): void => {
    const formKeyAssetField: string = FormService.getIndexedFormFieldKey(assetFieldId);

    if (assetFieldItems) {
      const newAssetFieldItems: Record<string, AssetFieldInput> = { ...assetFieldItems };
      delete newAssetFieldItems[assetFieldId];
      setAssetFieldItems(newAssetFieldItems);
    }

    if (assetFieldNewItemIds.length) {
      const assetFieldNewItemIdIndex: number = assetFieldNewItemIds.indexOf(assetFieldId);

      if (assetFieldNewItemIdIndex !== FormatService.INDEX_NOT_FOUND) {
        StructureService.deleteListItemByIndex(assetFieldNewItemIds, assetFieldNewItemIdIndex);
      }

      setAssetFieldNewItemIds([...assetFieldNewItemIds]);
    }

    changeForm(`${formPrefix}.options[${formKeyAssetField}]`, null);
  }, [assetFieldItems, assetFieldNewItemIds]);

  const onAddNewAssetAllocationItem: Function = useCallback((): void => {
    const newAssetAllocationItemId: number = StructureService.getNewItemId(
      [],
      (assetAllocationItems && Object.values(assetAllocationItems)) || [],
    );
    const formKeyAssetAllocationItem: string = FormService.getIndexedFormFieldKey(newAssetAllocationItemId);

    const newAssetAllocationItem: AssetAllocationItemInput = {
      id: newAssetAllocationItemId,
      portfolioId: null,
      value: FormatService.NUMBER_VALUE_DEFAULT.toString(),
    };

    setAssetAllocationItems({
      ...(assetAllocationItems || {}),
      [newAssetAllocationItem.id]: newAssetAllocationItem,
    });

    setAssetAllocationNewItemIds([
      ...assetAllocationNewItemIds,
      newAssetAllocationItem.id,
    ]);

    changeForm(`${formPrefix}.allocationItems[${formKeyAssetAllocationItem}]`, newAssetAllocationItem);
  }, [assetAllocationItems, assetAllocationNewItemIds]);

  const onDeleteAssetAllocationItemClick: Function = useCallback((assetAllocationItemId: number): void => {
    const formKeyAssetAllocationItem: string = FormService.getIndexedFormFieldKey(assetAllocationItemId);

    if (assetAllocationItems) {
      const newAssetAllocationItems: Record<string, AssetAllocationItemInput> = { ...assetAllocationItems };
      delete newAssetAllocationItems[assetAllocationItemId];
      setAssetAllocationItems({ ...newAssetAllocationItems });
    }

    if (assetAllocationNewItemIds.length) {
      const assetAllocationNewItemIdIndex: number = assetAllocationNewItemIds.indexOf(assetAllocationItemId);

      if (assetAllocationNewItemIdIndex !== FormatService.INDEX_NOT_FOUND) {
        StructureService.deleteListItemByIndex(assetAllocationNewItemIds, assetAllocationNewItemIdIndex);
      }

      setAssetAllocationNewItemIds([...assetAllocationNewItemIds]);
    }

    changeForm(`${formPrefix}.allocationItems[${formKeyAssetAllocationItem}]`, null);
  }, [assetAllocationItems, assetAllocationNewItemIds]);

  const onAssetAllocationPortfolioChange: Function = useCallback((
    portfolioId: number, assetAllocatedItemId: number,
  ): void => {
    if (assetAllocationItems) {
      setAssetAllocationItems({
        ...assetAllocationItems,
        [assetAllocatedItemId]: {
          ...assetAllocationItems[assetAllocatedItemId],
          portfolioId: portfolioId,
        },
      });
    }
  }, [assetAllocationItems]);

  const onOpenNewSellerForm: Function = useCallback((): void => {
    setIsNewSellerVisible(true);
  }, []);

  const onCloseNewSellerForm: Function = useCallback((): void => {
    setIsNewSellerVisible(false);
  }, []);

  const openDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(true);
  }, []);

  const closeDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
  }, []);

  const onDeleteClick: Function = useCallback((): void => {
    openDeleteConfirmModal();
  }, []);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!assetId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteAssetList({
        assetIds: [assetId],
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        setTimeout((): void => {
          navigate({
            pathname: ROUTE_ASSET_LIST,
          });
        }, TIMEOUT_SHORT);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [assetId]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const assetFormItem: FormValueItem = {
      ...values.assetData,
    };

    delete assetFormItem.location;
    delete assetFormItem.streetAddress;
    delete assetFormItem.postalCode;

    assetFormItem.types = assetFormItem.types && assetFormItem.types.length
      ? assetFormItem.types.map((assetTypeItem: AssetType): number => assetTypeItem.id) : [];
    assetFormItem.size = assetFormItem.size ? FormatService.formatCleanNumber(assetFormItem.size) : 0;
    assetFormItem.loanAmount = assetFormItem.loanAmount
      ? FormatService.formatCleanNumber(assetFormItem.loanAmount) : 0;
    assetFormItem.amount = assetFormItem.amount ? FormatService.formatCleanNumber(assetFormItem.amount) : 0;
    assetFormItem.value = assetFormItem.value ? FormatService.formatCleanNumber(assetFormItem.value) : 0;
    assetFormItem.cost = assetFormItem.cost ? FormatService.formatCleanNumber(assetFormItem.cost) : 0;
    assetFormItem.currentNoi = assetFormItem.currentNoi
      ? FormatService.formatCleanNumber(assetFormItem.currentNoi) : 0;
    assetFormItem.ttmNoi = assetFormItem.ttmNoi
      ? FormatService.formatCleanNumber(assetFormItem.ttmNoi) : 0;
    assetFormItem.runRateNoi = assetFormItem.runRateNoi
      ? FormatService.formatCleanNumber(assetFormItem.runRateNoi) : 0;
    assetFormItem.forwardNoi = assetFormItem.forwardNoi
      ? FormatService.formatCleanNumber(assetFormItem.forwardNoi) : 0;
    assetFormItem.maturityDate = assetFormItem.maturityDate
      ? DateService.formatDate(assetFormItem.maturityDate) : null;
    assetFormItem.sellerIds = assetFormItem.sellerIds && assetFormItem.sellerIds.length
      ? assetFormItem.sellerIds.map((contactItem: Contact): number => contactItem.id) : [];
    assetFormItem.tags = assetFormItem.tags && assetFormItem.tags.length
      ? assetFormItem.tags.map((tagItem: Tag): string => tagItem.name) : [];
    assetFormItem.portfolioInvestedEquity = assetFormItem.portfolioInvestedEquity
      ? FormatService.formatCleanNumber(assetFormItem.portfolioInvestedEquity) : 0;
    assetFormItem.leveredIrr = assetFormItem.leveredIrr
      ? FormatService.formatCleanNumber(assetFormItem.leveredIrr, true) : 0;
    assetFormItem.equityMultiple = assetFormItem.equityMultiple
      ? FormatService.formatCleanNumber(assetFormItem.equityMultiple, true) : 0;
    assetFormItem.futureNoi = assetFormItem.futureNoi
      ? FormatService.formatCleanNumber(assetFormItem.futureNoi) : 0;
    assetFormItem.investmentAmountCommitted = assetFormItem.investmentAmountCommitted
      ? FormatService.formatCleanNumber(assetFormItem.investmentAmountCommitted) : 0;
    assetFormItem.investmentAmountUnfunded = assetFormItem.investmentAmountUnfunded
      ? FormatService.formatCleanNumber(assetFormItem.investmentAmountUnfunded) : 0;
    assetFormItem.investmentAmountFunded = assetFormItem.investmentAmountFunded
      ? FormatService.formatCleanNumber(assetFormItem.investmentAmountFunded) : 0;
    assetFormItem.currentMarketValue = assetFormItem.currentMarketValue
      ? FormatService.formatCleanNumber(assetFormItem.currentMarketValue) : 0;
    assetFormItem.closeDate = assetFormItem.closeDate
      ? DateService.formatDate(assetFormItem.closeDate) : null;
    assetFormItem.amountInvested = assetFormItem.amountInvested
      ? FormatService.formatCleanNumber(assetFormItem.amountInvested) : 0;
    assetFormItem.projectedStabilizedNoi = assetFormItem.projectedStabilizedNoi
      ? FormatService.formatCleanNumber(assetFormItem.projectedStabilizedNoi) : 0;
    assetFormItem.goingInCapRate = assetFormItem.goingInCapRate
      ? FormatService.formatCleanNumber(assetFormItem.goingInCapRate, true) : 0;
    assetFormItem.untrendedYieldToCost = assetFormItem.untrendedYieldToCost
      ? FormatService.formatCleanNumber(assetFormItem.untrendedYieldToCost) : 0;
    assetFormItem.projectedUnleveredIrr = assetFormItem.projectedUnleveredIrr
      ? FormatService.formatCleanNumber(assetFormItem.projectedUnleveredIrr, true) : 0;
    assetFormItem.projectedUnleveredEquityMultiple = assetFormItem.projectedUnleveredEquityMultiple
      ? FormatService.formatCleanNumber(assetFormItem.projectedUnleveredEquityMultiple) : 0;
    assetFormItem.projectedUnleveredNetProfit = assetFormItem.projectedUnleveredNetProfit
      ? FormatService.formatCleanNumber(assetFormItem.projectedUnleveredNetProfit) : 0;
    assetFormItem.projectedLeveredIrr = assetFormItem.projectedLeveredIrr
      ? FormatService.formatCleanNumber(assetFormItem.projectedLeveredIrr, true) : 0;
    assetFormItem.projectedLeveredEquityMultiple = assetFormItem.projectedLeveredEquityMultiple
      ? FormatService.formatCleanNumber(assetFormItem.projectedLeveredEquityMultiple) : 0;
    assetFormItem.projectedLeveredNetProfit = assetFormItem.projectedLeveredNetProfit
      ? FormatService.formatCleanNumber(assetFormItem.projectedLeveredNetProfit) : 0;
    assetFormItem.seniorLoanAmount = assetFormItem.seniorLoanAmount
      ? FormatService.formatCleanNumber(assetFormItem.seniorLoanAmount) : 0;
    assetFormItem.seniorLoanClosingDate = assetFormItem.seniorLoanClosingDate
      ? DateService.formatDate(assetFormItem.seniorLoanClosingDate) : null;
    assetFormItem.seniorLoanMaturityDate = assetFormItem.seniorLoanMaturityDate
      ? DateService.formatDate(assetFormItem.seniorLoanMaturityDate) : null;
    assetFormItem.seniorLoanInterestSpread = assetFormItem.seniorLoanInterestSpread
      ? FormatService.formatCleanNumber(assetFormItem.seniorLoanInterestSpread, true) : 0;
    assetFormItem.seniorLoanLtv = assetFormItem.seniorLoanLtv
      ? FormatService.formatCleanNumber(assetFormItem.seniorLoanLtv, true) : 0;
    assetFormItem.seniorLoanLtc = assetFormItem.seniorLoanLtc
      ? FormatService.formatCleanNumber(assetFormItem.seniorLoanLtc, true) : 0;
    assetFormItem.mezzanineLoanAmount = assetFormItem.mezzanineLoanAmount
      ? FormatService.formatCleanNumber(assetFormItem.mezzanineLoanAmount) : 0;
    assetFormItem.mezzanineLoanClosingDate = assetFormItem.mezzanineLoanClosingDate
      ? DateService.formatDate(assetFormItem.mezzanineLoanClosingDate) : null;
    assetFormItem.mezzanineLoanMaturityDate = assetFormItem.mezzanineLoanMaturityDate
      ? DateService.formatDate(assetFormItem.mezzanineLoanMaturityDate) : null;
    assetFormItem.mezzanineLoanInterestSpread = assetFormItem.mezzanineLoanInterestSpread
      ? FormatService.formatCleanNumber(assetFormItem.mezzanineLoanInterestSpread, true) : 0;
    assetFormItem.mezzanineLoanLtv = assetFormItem.mezzanineLoanLtv
      ? FormatService.formatCleanNumber(assetFormItem.mezzanineLoanLtv, true) : 0;
    assetFormItem.mezzanineLoanLtc = assetFormItem.mezzanineLoanLtc
      ? FormatService.formatCleanNumber(assetFormItem.mezzanineLoanLtc, true) : 0;
    assetFormItem.blendedLoanInterestSpread = assetFormItem.blendedLoanInterestSpread
      ? FormatService.formatCleanNumber(assetFormItem.blendedLoanInterestSpread, true) : 0;
    assetFormItem.notePrincipalBalanceAtOrigination = assetFormItem.notePrincipalBalanceAtOrigination
      ? FormatService.formatCleanNumber(assetFormItem.notePrincipalBalanceAtOrigination) : 0;
    assetFormItem.accruedInterest = assetFormItem.accruedInterest
      ? FormatService.formatCleanNumber(assetFormItem.accruedInterest) : 0;
    assetFormItem.yearBuilt = assetFormItem.yearBuilt
      ? FormatService.formatCleanNumber(assetFormItem.yearBuilt, false, true) : 0;
    assetFormItem.yearRenovated = assetFormItem.yearRenovated
      ? FormatService.formatCleanNumber(assetFormItem.yearRenovated, false, true) : 0;
    assetFormItem.firstPaymentDate = assetFormItem.firstPaymentDate
      ? DateService.formatDate(assetFormItem.firstPaymentDate) : null;
    assetFormItem.nextPaymentDueDate = assetFormItem.nextPaymentDueDate
      ? DateService.formatDate(assetFormItem.nextPaymentDueDate) : null;
    assetFormItem.originalLoanTermMonth = assetFormItem.originalLoanTermMonth
      ? FormatService.formatCleanNumber(assetFormItem.originalLoanTermMonth, false, true) : 0;
    assetFormItem.loanAge = assetFormItem.loanAge
      ? FormatService.formatCleanNumber(assetFormItem.loanAge, false, true) : 0;
    assetFormItem.remainingTerm = assetFormItem.remainingTerm
      ? FormatService.formatCleanNumber(assetFormItem.remainingTerm, false, true) : 0;
    assetFormItem.amortizationPeriodMonths = assetFormItem.amortizationPeriodMonths
      ? FormatService.formatCleanNumber(assetFormItem.amortizationPeriodMonths, false, true) : 0;
    assetFormItem.remainingAmortizationPeriod = assetFormItem.remainingAmortizationPeriod
      ? FormatService.formatCleanNumber(assetFormItem.remainingAmortizationPeriod, false, true) : 0;
    assetFormItem.interestOnlyPeriod = assetFormItem.interestOnlyPeriod
      ? FormatService.formatCleanNumber(assetFormItem.interestOnlyPeriod, false, true) : 0;
    assetFormItem.remainingInterestOnlyPeriod = assetFormItem.remainingInterestOnlyPeriod
      ? FormatService.formatCleanNumber(assetFormItem.remainingInterestOnlyPeriod, false, true) : 0;
    assetFormItem.lienPosition = assetFormItem.lienPosition
      ? FormatService.formatCleanNumber(assetFormItem.lienPosition, false, true) : 0;
    assetFormItem.currentCreditLetterAmount = assetFormItem.currentCreditLetterAmount
      ? FormatService.formatCleanNumber(assetFormItem.currentCreditLetterAmount) : 0;
    assetFormItem.currentCreditLetterCreditExpirationDate = assetFormItem.currentCreditLetterCreditExpirationDate
      ? DateService.formatDate(assetFormItem.currentCreditLetterCreditExpirationDate) : null;
    assetFormItem.interestReservesAtOrigination = assetFormItem.interestReservesAtOrigination
      ? FormatService.formatCleanNumber(assetFormItem.interestReservesAtOrigination) : 0;
    assetFormItem.taxInsuranceEscrowsAtOrigination = assetFormItem.taxInsuranceEscrowsAtOrigination
      ? FormatService.formatCleanNumber(assetFormItem.taxInsuranceEscrowsAtOrigination) : 0;
    assetFormItem.capitalExpenditureReservesAtOrigination = assetFormItem.capitalExpenditureReservesAtOrigination
      ? FormatService.formatCleanNumber(assetFormItem.capitalExpenditureReservesAtOrigination) : 0;
    assetFormItem.otherEscrowAndReservesAtOrigination = assetFormItem.otherEscrowAndReservesAtOrigination
      ? FormatService.formatCleanNumber(assetFormItem.otherEscrowAndReservesAtOrigination) : 0;
    assetFormItem.currentInterestReserves = assetFormItem.currentInterestReserves
      ? FormatService.formatCleanNumber(assetFormItem.currentInterestReserves) : 0;
    assetFormItem.currentTaxInsuranceEscrows = assetFormItem.currentTaxInsuranceEscrows
      ? FormatService.formatCleanNumber(assetFormItem.currentTaxInsuranceEscrows) : 0;
    assetFormItem.currentCapitalExpenditureEscrows = assetFormItem.currentCapitalExpenditureEscrows
      ? FormatService.formatCleanNumber(assetFormItem.currentCapitalExpenditureEscrows) : 0;
    assetFormItem.currentOtherEscrowsAndReserves = assetFormItem.currentOtherEscrowsAndReserves
      ? FormatService.formatCleanNumber(assetFormItem.currentOtherEscrowsAndReserves) : 0;
    assetFormItem.initialAppraisalDate = assetFormItem.initialAppraisalDate
      ? DateService.formatDate(assetFormItem.initialAppraisalDate) : null;
    assetFormItem.stabilizedAppraisedNoi = assetFormItem.stabilizedAppraisedNoi
      ? FormatService.formatCleanNumber(assetFormItem.stabilizedAppraisedNoi) : 0;
    assetFormItem.stabilizedOccupancy = assetFormItem.stabilizedOccupancy
      ? FormatService.formatCleanNumber(assetFormItem.stabilizedOccupancy, true) : 0;
    assetFormItem.stabilizedCapitalizationRate = assetFormItem.stabilizedCapitalizationRate
      ? FormatService.formatCleanNumber(assetFormItem.stabilizedCapitalizationRate, true) : 0;
    assetFormItem.mostRecentAppraisalDate = assetFormItem.mostRecentAppraisalDate
      ? DateService.formatDate(assetFormItem.mostRecentAppraisalDate) : null;
    assetFormItem.atCloseRevenue = assetFormItem.atCloseRevenue
      ? FormatService.formatCleanNumber(assetFormItem.atCloseRevenue) : 0;
    assetFormItem.atCloseExpenses = assetFormItem.atCloseExpenses
      ? FormatService.formatCleanNumber(assetFormItem.atCloseExpenses) : 0;
    assetFormItem.atCloseNoi = assetFormItem.atCloseNoi
      ? FormatService.formatCleanNumber(assetFormItem.atCloseNoi) : 0;
    assetFormItem.atCloseDebtYieldNoi = assetFormItem.atCloseDebtYieldNoi
      ? FormatService.formatCleanNumber(assetFormItem.atCloseDebtYieldNoi, true) : 0;
    assetFormItem.occupancyAtOrigination = assetFormItem.occupancyAtOrigination
      ? FormatService.formatCleanNumber(assetFormItem.occupancyAtOrigination, true) : 0;
    assetFormItem.atCloseNoiAndOccupancyDate = assetFormItem.atCloseNoiAndOccupancyDate
      ? DateService.formatDate(assetFormItem.atCloseNoiAndOccupancyDate) : null;
    assetFormItem.firstExtendedMaturityDate = assetFormItem.firstExtendedMaturityDate
      ? DateService.formatDate(assetFormItem.firstExtendedMaturityDate) : null;
    assetFormItem.secondExtendedMaturityDate = assetFormItem.secondExtendedMaturityDate
      ? DateService.formatDate(assetFormItem.secondExtendedMaturityDate) : null;
    assetFormItem.thirdExtendedMaturityDate = assetFormItem.thirdExtendedMaturityDate
      ? DateService.formatDate(assetFormItem.thirdExtendedMaturityDate) : null;

    if (assetFormItem.seller && !Object.keys(assetFormItem.seller).length) {
      delete assetFormItem.seller;
    }

    if (assetPhotoFile) {
      assetFormItem.mainPhoto = assetPhotoFile;
    } else if (isAssetPhotoDeleted) {
      assetFormItem.mainPhoto = null;
    } else {
      delete assetFormItem.mainPhoto;
    }

    const assetOptions: AssetFieldInput[] = [];
    if (assetFormItem.options && Object.keys(assetFormItem.options).length) {
      Object.keys(assetFormItem.options).forEach((assetFieldIndex: string): void => {
        assetOptions.push({
          ...assetFormItem.options[assetFieldIndex],
        });
      });
    }
    assetFormItem.options = assetOptions;

    const assetAllocationItems: AssetAllocationItemInput[] = [];
    if (assetFormItem.allocationItems && Object.keys(assetFormItem.allocationItems).length) {
      Object.keys(assetFormItem.allocationItems).forEach((assetAllocationItemIndex: string): void => {
        const assetAllocationItem: FormValueItem = (
          assetFormItem.allocationItems[assetAllocationItemIndex]
        );

        if (assetAllocationItem) {
          assetAllocationItems.push({
            ...assetAllocationItem,
            value: FormatService.formatCleanNumber(assetAllocationItem.value, true, true),
          });
        }
      });
    }
    assetFormItem.allocationItems = assetAllocationItems;

    const assetItem: AssetInput = assetFormItem;

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveAssetData({
        asset: assetItem,
      }, (response: ActionResponse): void => {
        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [assetPhotoFile, isAssetPhotoDeleted]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [assetPhotoFile, isAssetPhotoDeleted, formValues, withFormErrors]);

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_LOCATION_INFORMATION,
      title: 'Location Info',
      component: (
        <AssetLocationInformation
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isMultiplePortfolioGroupView={isMultiplePortfolioGroupView}
          formError={formError}
          assetData={assetData}
          portfolioList={portfolioList}
          onDealClick={onDealClick}
          onAddressFieldKeyDown={onAddressFieldKeyDown}
          onAddressSearchCompleted={onAddressSearchCompleted}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_PROPERTY_INFORMATION,
      title: 'Property Info',
      component: (
        <AssetPropertyInformation
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          assetData={assetData}
          measureUnitList={measureUnitList}
          tagList={tagList}
          assetTypeList={assetTypeList}
          selectedAssetTypes={selectedAssetTypes}
          selectedTags={selectedTags}
          onAssetTypesChange={onAssetTypesChange}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onTagClick={onTagClick}
          onTagsChange={onTagsChange}
          onTagSearch={onTagSearch}
          onTagSearchClose={onTagSearchClose}
        />
      ),
    },
    {
      id: 3,
      name: TAB_NAME_PROPERTY_CAPITALIZATION,
      title: 'Property Capitalization',
      component: (
        <AssetPropertyCapitalization
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          assetData={assetData}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 4,
      name: TAB_NAME_OWNERSHIP_INFORMATION,
      title: 'Ownership Info',
      component: (
        <AssetOwnershipInformation
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          assetData={assetData}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 5,
      name: TAB_NAME_INVESTMENT_INFORMATION,
      title: 'Investment Info',
      component: (
        <AssetInvestmentInformation
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          assetData={assetData}
          pricingTypeList={pricingTypeList}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 6,
      name: TAB_NAME_INVESTMENT_PERFORMANCE,
      title: 'Investment Performance',
      component: (
        <AssetInvestmentPerformance
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          assetData={assetData}
          assetStatusList={assetStatusList}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 7,
      name: TAB_NAME_PROPERTY_PERFORMANCE,
      title: 'Property Performance',
      component: (
        <AssetPropertyPerformance
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          assetData={assetData}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 8,
      name: TAB_NAME_MANAGEMENT_FEE_INFORMATION,
      title: 'Management Fee Info',
      component: (
        <AssetManagementFeeInformation
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          assetData={assetData}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 9,
      name: TAB_NAME_ALLOCATION,
      title: 'Allocation',
      component: (
        <AssetAllocation
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isMultiplePortfolioGroupView={isMultiplePortfolioGroupView}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          assetData={assetData}
          assetAllocationItems={assetAllocationItems}
          assetAllocationNewItemIds={assetAllocationNewItemIds}
          portfolioList={portfolioList}
          hiddenPortfolioList={hiddenPortfolioList}
          onAddNewAssetAllocationItem={onAddNewAssetAllocationItem}
          onAssetAllocationPortfolioChange={onAssetAllocationPortfolioChange}
          onDeleteAssetAllocationItemClick={onDeleteAssetAllocationItemClick}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 10,
      name: TAB_NAME_OTHER,
      title: 'Other',
      component: (
        <AssetOther
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isNewSellerVisible={isNewSellerVisible}
          formError={formError}
          assetData={assetData}
          assetFieldItems={assetFieldItems}
          assetFieldNewItemIds={assetFieldNewItemIds}
          selectedSellers={selectedSellers}
          selectedAssetTypes={selectedAssetTypes}
          contactCompanyList={contactCompanyList}
          assetOwnershipStructureTypeList={assetOwnershipStructureTypeList}
          assetStatusList={assetStatusList}
          assetTypeList={assetTypeList}
          onAssetTypesChange={onAssetTypesChange}
          onSellersChange={onSellersChange}
          onContactClick={onContactClick}
          onOpenNewSellerForm={onOpenNewSellerForm}
          onCloseNewSellerForm={onCloseNewSellerForm}
          onAssetFieldAddNewItem={onAssetFieldAddNewItem}
          onAssetFieldDeleteClick={onAssetFieldDeleteClick}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
        />
      ),
    },
  ];

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        navigate({
          pathname: `${ROUTE_ASSET}/${assetId}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [locationQueryParams, tabs, assetId]);

  const onBeforeTabChange: Function = useCallback((newTabIndex: number): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastTabIndex(newTabIndex);
    }
  }, [isFormChanged]);

  const onRefresh: Function = useCallback((): void => {
    if (assetId) {
      dispatch(getAssetData({
        assetId,
      }, undefined, (exception: ServerError): void => {
        if (exception) {
          setErrorMessage(exception.message);
        }
      }));
    }
  }, [assetId]);

  const onResetAssetData: Function = useCallback((): void => {
    dispatch(resetAssetData());
  }, []);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): ReactUseEffectCleanUp => {
    if (isDataLoaded) {
      onResetAssetData();
    }

    if (assetId) {
      dispatch(getAssetData({
        assetId,
      }, undefined, (exception: ServerError): void => {
        if (exception) {
          setErrorMessage(exception.message);
        }
      }));
    }

    return (): void => {
      onResetAssetData();
    };
  }, [assetId]);

  useEffect((): void => {
    if (assetData) {
      const initialAssetFieldItems: Record<string, AssetField> = {};
      if (assetData.options && assetData.options.length) {
        assetData.options.forEach((assetFieldItem: AssetField): void => {
          initialAssetFieldItems[assetFieldItem.id] = { ...assetFieldItem };
        });
      }
      setAssetFieldItems(initialAssetFieldItems);

      const initialAssetAllocationItems: Record<string, AssetAllocationItem> = {};
      if (assetData.allocationItems && assetData.allocationItems.length) {
        assetData.allocationItems.forEach((assetAllocationItem: AssetAllocationItem): void => {
          initialAssetAllocationItems[assetAllocationItem.id] = {
            id: assetAllocationItem.id,
            portfolioId: assetAllocationItem.portfolio ? assetAllocationItem.portfolio.id : null,
            portfolio: assetAllocationItem.portfolio || null,
            value: assetAllocationItem.value,
          };
        });
      }
      setAssetAllocationItems(initialAssetAllocationItems);

      if (!selectedSellers) {
        setSelectedSellers(assetData.sellers || []);
      }

      if (!selectedAssetTypes) {
        setSelectedAssetTypes(assetData.types || []);
      }

      if (!selectedTags) {
        setSelectedTags(assetData.tags || []);
      }

      setAssetAddress(assetData.address || null);
    }
  }, [assetData, isEditMode]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastTabIndex !== null && lastTabIndex >= TAB_INDEX_DEFAULT) {
        const queryParams: LocationQueryParams = locationQueryParams || {};
        const currentTab: TabItem = tabs[lastTabIndex];

        if (currentTab) {
          queryParams[QUERY_PARAM_TAB] = currentTab.name;

          navigate({
            pathname: `${ROUTE_ASSET}/${assetId}`,
            search: FormatService.formatQueryParams(queryParams),
          });
        }

        setLastTabIndex(null);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (lastLocation) {
      setLastTabIndex(null);
    }
  }, [lastLocation]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  if (!assetId) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  if (errorMessage) {
    return (
      <PageError message={errorMessage} />
    );
  }

  if (!isDataLoaded) {
    return <Loader isPageLoader />;
  }

  return (
    <CheckAccess permissions={assetViewPermissions}>
      <Form id={FORM_ID_ASSET} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
          <ContentHeader editPermission={ASSET_EDIT} title={pageTitle} />
          <Controls
            withDelete
            isSaveDisabled={isSaveDisabled}
            editPermissions={assetEditPermissions}
            onCancelForm={onCancelForm}
            onSubmitForm={onSubmitForm}
            deletePermission={ASSET_DELETE}
            onDeleteClick={onDeleteClick}
          />
          <PageContainer>
            <PageContainerSide>
              <ImageContainer
                isImageDeleted={isAssetPhotoDeleted}
                isEditMode={isEditMode}
                name={assetData.name}
                image={assetData.mainPhoto}
                imageThumbnail={assetData.mainPhotoThumbnail}
                imageFile={assetPhotoFile}
                imageFormFieldName={`${formPrefix}.mainPhoto`}
                defaultImage={AssetDefaultImage}
                onImageFileChange={onAssetPhotoFileChange}
                onImageFileDelete={onAssetPhotoFileDelete}
              />
            </PageContainerSide>
            <PageContainerMain>
              <LimitedPageContainer isActive>
                <PageTabs
                  isCompressed
                  isDisabled={isFormChanged}
                  tabs={tabs}
                  currentTab={queryTab}
                  onTabChange={onTabChange}
                  onBeforeTabChange={onBeforeTabChange}
                />
              </LimitedPageContainer>
            </PageContainerMain>
          </PageContainer>
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError={isEditMode}
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeleteConfirm}
          onCloseModal={closeDeleteConfirmModal}
        />
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onCloseModal={closeLeaveConfirmModal}
          onConfirm={onLeaveConfirm}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    asset,
    controls,
    rightDrawer,
  } = reducers;

  const assetData: Asset = asset.assetData && asset.assetData.asset;

  if (assetData) {
    initialValues.assetData = {
      id: assetData.id,
      name: assetData.name,
      status: assetData.status,
      size: FormatService.formatNumber(assetData.size),
      sizeType: assetData.sizeType ? assetData.sizeType.name : null,
      loanAmount: FormatService.formatNumber(assetData.loanAmount),
      amount: FormatService.formatNumber(assetData.amount),
      value: FormatService.formatNumber(assetData.value),
      addressDetails: assetData.addressDetails,
      parcelNumber: assetData.parcelNumber,
      submarket: assetData.submarket,
      lotSize: assetData.lotSize,
      assetOwnershipStructure: assetData.assetOwnershipStructure,
      affordableUnits: assetData.affordableUnits,
      asOfRightZoning: assetData.asOfRightZoning,
      cost: FormatService.formatNumber(assetData.cost),
      occupancy: assetData.occupancy,
      currentNoi: FormatService.formatNumber(assetData.currentNoi),
      ttmNoi: FormatService.formatNumber(assetData.ttmNoi),
      runRateNoi: FormatService.formatNumber(assetData.runRateNoi),
      forwardNoi: FormatService.formatNumber(assetData.forwardNoi),
      maturityDate: assetData.maturityDate ? DateService.parseDate(assetData.maturityDate) : '',
      portfolioId: assetData.portfolio ? assetData.portfolio.id : null,
      streetAddress: assetData.streetAddress,
      location: assetData.location,
      postalCode: assetData.postalCode,
      sellerIds: assetData.sellers && assetData.sellers.length
        ? assetData.sellers.map((contactItem: Contact): Contact => ({
          id: contactItem.id,
          name: contactItem.name,
          fullName: contactItem.fullName,
          photo: contactItem.photo,
          photoThumbnail: contactItem.photoThumbnail,
        })) : [],
      portfolioInvestedEquity: FormatService.formatNumber(assetData.portfolioInvestedEquity),
      leveredIrr: FormatService.formatNumber(assetData.leveredIrr, true),
      loanNumber: assetData.loanNumber,
      equityMultiple: FormatService.formatNumber(assetData.equityMultiple, true),
      leedDesignation: assetData.leedDesignation,
      ownerEntityName: assetData.ownerEntityName,
      futureNoi: FormatService.formatNumber(assetData.futureNoi),
      investmentEntityName: assetData.investmentEntityName,
      investmentAmountCommitted: FormatService.formatNumber(assetData.investmentAmountCommitted),
      investmentAmountUnfunded: FormatService.formatNumber(assetData.investmentAmountUnfunded),
      investmentAmountFunded: FormatService.formatNumber(assetData.investmentAmountFunded),
      currentMarketValue: FormatService.formatNumber(assetData.currentMarketValue),
      closeDate: assetData.closeDate ? DateService.parseDate(assetData.closeDate) : '',
      extensions: assetData.extensions,
      amountInvested: FormatService.formatNumber(assetData.amountInvested),
      projectedStabilizedNoi: FormatService.formatNumber(assetData.projectedStabilizedNoi),
      goingInCapRate: FormatService.formatNumber(assetData.goingInCapRate, true),
      untrendedYieldToCost: FormatService.formatNumber(assetData.untrendedYieldToCost),
      projectedUnleveredIrr: FormatService.formatNumber(assetData.projectedUnleveredIrr, true),
      projectedUnleveredEquityMultiple: FormatService.formatNumber(assetData.projectedUnleveredEquityMultiple),
      projectedUnleveredNetProfit: FormatService.formatNumber(assetData.projectedUnleveredNetProfit),
      projectedLeveredIrr: FormatService.formatNumber(assetData.projectedLeveredIrr, true),
      projectedLeveredEquityMultiple: FormatService.formatNumber(assetData.projectedLeveredEquityMultiple),
      projectedLeveredNetProfit: FormatService.formatNumber(assetData.projectedLeveredNetProfit),
      seniorLenderName: assetData.seniorLenderName,
      seniorLoanAmount: FormatService.formatNumber(assetData.seniorLoanAmount),
      seniorLoanClosingDate: assetData.seniorLoanClosingDate
        ? DateService.parseDate(assetData.seniorLoanClosingDate) : '',
      seniorLoanMaturityDate: assetData.seniorLoanMaturityDate
        ? DateService.parseDate(assetData.seniorLoanMaturityDate) : '',
      seniorLoanInterestRate: assetData.seniorLoanInterestRate,
      seniorLoanInterestSpread: FormatService.formatNumber(assetData.seniorLoanInterestSpread, true),
      seniorLoanLtv: FormatService.formatNumber(assetData.seniorLoanLtv, true),
      seniorLoanLtc: FormatService.formatNumber(assetData.seniorLoanLtc, true),
      mezzanineLenderName: assetData.mezzanineLenderName,
      mezzanineLoanAmount: FormatService.formatNumber(assetData.mezzanineLoanAmount),
      mezzanineLoanClosingDate: assetData.mezzanineLoanClosingDate
        ? DateService.parseDate(assetData.mezzanineLoanClosingDate) : '',
      mezzanineLoanMaturityDate: assetData.mezzanineLoanMaturityDate
        ? DateService.parseDate(assetData.mezzanineLoanMaturityDate) : '',
      mezzanineLoanInterestRate: assetData.mezzanineLoanInterestRate,
      mezzanineLoanInterestSpread: FormatService.formatNumber(assetData.mezzanineLoanInterestSpread, true),
      mezzanineLoanLtv: FormatService.formatNumber(assetData.mezzanineLoanLtv, true),
      mezzanineLoanLtc: FormatService.formatNumber(assetData.mezzanineLoanLtc, true),
      blendedLoanInterestRate: assetData.blendedLoanInterestRate,
      blendedLoanInterestSpread: FormatService.formatNumber(assetData.blendedLoanInterestSpread, true),
      notePrincipalBalanceAtOrigination: FormatService.formatNumber(assetData.notePrincipalBalanceAtOrigination),
      accruedInterest: FormatService.formatNumber(assetData.accruedInterest),
      marketType: assetData.marketType,
      yearBuilt: FormatService.formatNumber(assetData.yearBuilt, true),
      yearRenovated: FormatService.formatNumber(assetData.yearRenovated, true),
      firstPaymentDate: assetData.firstPaymentDate ? DateService.parseDate(assetData.firstPaymentDate) : '',
      nextPaymentDueDate: assetData.nextPaymentDueDate ? DateService.parseDate(assetData.nextPaymentDueDate) : '',
      originalLoanTermMonth: FormatService.formatNumber(assetData.originalLoanTermMonth, true),
      loanAge: FormatService.formatNumber(assetData.loanAge, true),
      remainingTerm: FormatService.formatNumber(assetData.remainingTerm, true),
      amortizationPeriodMonths: FormatService.formatNumber(assetData.amortizationPeriodMonths, true),
      remainingAmortizationPeriod: FormatService.formatNumber(assetData.remainingAmortizationPeriod, true),
      paymentStringYear: assetData.paymentStringYear,
      amortizationType: assetData.amortizationType,
      interestOnlyPeriod: FormatService.formatNumber(assetData.interestOnlyPeriod, true),
      remainingInterestOnlyPeriod: FormatService.formatNumber(assetData.remainingInterestOnlyPeriod, true),
      interestAccrualMethod: assetData.interestAccrualMethod,
      primaryLoanPurpose: assetData.primaryLoanPurpose,
      underlyingLoanType: assetData.underlyingLoanType,
      lienPosition: FormatService.formatNumber(assetData.lienPosition, true),
      primaryCollateral: assetData.primaryCollateral,
      securityInterest: assetData.securityInterest,
      groundLease: assetData.groundLease,
      prepaymentProvisions: assetData.prepaymentProvisions,
      remainingPrepaymentProvisions: assetData.remainingPrepaymentProvisions,
      currentCreditLetterAmount: FormatService.formatNumber(assetData.currentCreditLetterAmount),
      currentCreditLetterCreditExpirationDate: assetData.currentCreditLetterCreditExpirationDate
        ? DateService.parseDate(assetData.currentCreditLetterCreditExpirationDate) : '',
      interestReservesAtOrigination: FormatService.formatNumber(assetData.interestReservesAtOrigination),
      taxInsuranceEscrowsAtOrigination: FormatService.formatNumber(assetData.taxInsuranceEscrowsAtOrigination),
      capitalExpenditureReservesAtOrigination: FormatService.formatNumber(
        assetData.capitalExpenditureReservesAtOrigination,
      ),
      otherEscrowAndReservesAtOrigination: FormatService.formatNumber(assetData.otherEscrowAndReservesAtOrigination),
      currentInterestReserves: FormatService.formatNumber(assetData.currentInterestReserves),
      currentTaxInsuranceEscrows: FormatService.formatNumber(assetData.currentTaxInsuranceEscrows),
      currentCapitalExpenditureEscrows: FormatService.formatNumber(assetData.currentCapitalExpenditureEscrows),
      currentOtherEscrowsAndReserves: FormatService.formatNumber(assetData.currentOtherEscrowsAndReserves),
      initialAppraisalDate: assetData.initialAppraisalDate ? DateService.parseDate(assetData.initialAppraisalDate) : '',
      stabilizedAppraisedNoi: FormatService.formatNumber(assetData.stabilizedAppraisedNoi),
      stabilizedOccupancy: FormatService.formatNumber(assetData.stabilizedOccupancy, true),
      stabilizedCapitalizationRate: FormatService.formatNumber(assetData.stabilizedCapitalizationRate, true),
      mostRecentAppraisalDate: assetData.mostRecentAppraisalDate
        ? DateService.parseDate(assetData.mostRecentAppraisalDate) : '',
      atCloseRevenue: FormatService.formatNumber(assetData.atCloseRevenue),
      atCloseExpenses: FormatService.formatNumber(assetData.atCloseExpenses),
      atCloseNoi: FormatService.formatNumber(assetData.atCloseNoi),
      atCloseDebtYieldNoi: FormatService.formatNumber(assetData.atCloseDebtYieldNoi, true),
      atCloseDscrNoi: assetData.atCloseDscrNoi,
      atCloseNcf: assetData.atCloseNcf,
      atCloseDebtYieldNcf: assetData.atCloseDebtYieldNcf,
      atCloseDscrNcf: assetData.atCloseDscrNcf,
      occupancyAtOrigination: FormatService.formatNumber(assetData.occupancyAtOrigination, true),
      atCloseNoiAndOccupancyDate: assetData.atCloseNoiAndOccupancyDate
        ? DateService.parseDate(assetData.atCloseNoiAndOccupancyDate) : '',
      stabilizedNoiAtOrigination: assetData.stabilizedNoiAtOrigination,
      stabilizedNcfAtOrigination: assetData.stabilizedNcfAtOrigination,
      firstExtendedMaturityDate: assetData.firstExtendedMaturityDate
        ? DateService.parseDate(assetData.firstExtendedMaturityDate) : '',
      secondExtendedMaturityDate: assetData.secondExtendedMaturityDate
        ? DateService.parseDate(assetData.secondExtendedMaturityDate) : '',
      thirdExtendedMaturityDate: assetData.thirdExtendedMaturityDate
        ? DateService.parseDate(assetData.thirdExtendedMaturityDate) : '',
      yardiAssetId: assetData.yardiAssetId,
      showInPortfolio: assetData.showInPortfolio,
      types: assetData.types,
      tags: assetData.tags,
      address: assetData.address,
      options: {},
      allocationItems: {},
      mainPhoto: null,
      seller: {},
    };

    if (assetData.options && assetData.options.length) {
      assetData.options.forEach((assetFieldItem: AssetField): void => {
        const formKeyAssetField: string = FormService.getIndexedFormFieldKey(assetFieldItem.id);

        initialValues.assetData.options[formKeyAssetField] = {
          name: assetFieldItem.name,
          value: assetFieldItem.value,
        };
      });
    }

    if (assetData.allocationItems && assetData.allocationItems.length) {
      assetData.allocationItems.forEach((assetAllocationItem: AssetAllocationItem): void => {
        const formKeyAssetAllocationItem: string = FormService.getIndexedFormFieldKey(assetAllocationItem.id);

        initialValues.assetData.allocationItems[formKeyAssetAllocationItem] = {
          id: assetAllocationItem.id,
          portfolioId: assetAllocationItem.portfolio ? assetAllocationItem.portfolio.id : null,
          value: assetAllocationItem.value
            ? FormatService.formatNumber(assetAllocationItem.value, true, undefined, true) : null,
        };
      });
    }
  }

  return {
    isDataLoaded: asset.assetData && !!asset.assetData.asset,
    isEditMode: controls.isEditMode,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    assetData: asset.assetData && asset.assetData.asset,
    portfolioList: asset.assetData && asset.assetData.portfolioList,
    hiddenPortfolioList: asset.assetData && asset.assetData.hiddenPortfolioList,
    assetTypeList: asset.assetData && asset.assetData.assetTypeList,
    assetStatusList: asset.assetData && asset.assetData.assetStatusList,
    measureUnitList: asset.assetData && asset.assetData.measureUnitList,
    pricingTypeList: asset.assetData && asset.assetData.pricingTypeList,
    assetOwnershipStructureTypeList: asset.assetData && asset.assetData.assetOwnershipStructureTypeList,
    contactCompanyList: asset.assetData && asset.assetData.contactCompanyList,
    tagList: asset.assetData && asset.assetData.tagList,
  };
};

const AssetForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ asset }: Reducers): ValidationErrorsMap => (
    asset.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ asset }: Reducers): ValidationErrorsMap => (
    asset.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_ASSET}
      component={Asset}
      validator={AssetValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(AssetForm);
